import { Add, AttachFile, Science } from '@mui/icons-material';

import ArrowForwardIconOutlined from '@mui/icons-material/ArrowForward';
import type { SxProps, TextFieldProps, Theme } from '@mui/material';
import {
  Box,
  Chip,
  CircularProgress,
  Fade,
  Grid,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Popover,
  Stack,
  SvgIcon,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import type {
  Control,
  UseFormGetValues,
  UseFormSetValue,
} from 'react-hook-form';
import { Controller, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { use, useState, useSyncExternalStore } from 'react';

import owlWandIcon from 'Images/owl-wand.svg';

import { Or2Avatar } from '@/components/Avatar/Or2Avatar';

import { Or2ListItemButton } from '@/components/data-display/Or2ListItemButton';
import { CurrentUserContext } from '@/context/CurrentUserContext';
import { Or2Link } from '@/components/navigation/Or2Link';
import { env } from '@/env';
import { LEGAL_DOCUMENT_URLS } from '@/utils/legalDocuments';

import { Or2Button } from '@/components/button/Or2Button';

import { useTrackIntercomEvent } from '@/hooks/useTrackIntercomEvent';

import { useAssistantFilePicker } from '../../hooks/useAssistantFilePicker';

import { AssistantFilePickerDialog } from './AssistantFilePickerDialog';

export const AssistantSchema = z.object({
  prompt: z
    .string()
    .min(5, 'Your prompt must be at least 5 characters long')
    .max(
      10000,
      "Wow, that's a lot of text! We'd like you to keep it under 10,000 characters please",
    )
    .trim(),
  experimental: z.boolean(),
  fileIds: z.array(z.string()).max(5).optional(),
});

export type AssistantFormData = z.infer<typeof AssistantSchema>;

const keycapSx: SxProps<Theme> = {
  px: 0.5,
  border: 1,
  borderColor: 'divider',
  borderRadius: 0.5,
  bgcolor: 'action.hover',
  fontFamily: 'inherit',
  fontSize: '0.7rem',
};

const subscribeToNothing = () => () => undefined;

// Read via useSyncExternalStore so the server render (no navigator) and the
// first client render agree, avoiding a hydration mismatch.
const useSendShortcutModifier = () =>
  useSyncExternalStore(
    subscribeToNothing,
    () => (/Mac|iPhone|iPad/.test(navigator.userAgent) ? '⌘' : 'Ctrl'),
    () => 'Ctrl',
  );

interface Props {
  control: Control<AssistantFormData>;
  getValues: UseFormGetValues<AssistantFormData>;
  setValue: UseFormSetValue<AssistantFormData>;
  textFieldProps?: TextFieldProps;
  loading: boolean;
  disabled?: boolean;
  errorMessage: string | null | undefined;
  onSend: () => void;
}

export const AssistantForm = ({
  control,
  getValues,
  setValue,
  loading,
  textFieldProps = { placeholder: 'Type your message...' },
  disabled,
  errorMessage,
  onSend,
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [popoverGridDirection, setPopoverGridDirection] = useState<
    'column' | 'column-reverse'
  >(getValues('experimental') ? 'column' : 'column-reverse');

  const trackIntercomEvent = useTrackIntercomEvent();

  const handleSend = () => {
    onSend();
    trackIntercomEvent(`Clicked AI Assistant Send Prompt Button`);
  };

  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const sendShortcutModifier = useSendShortcutModifier();
  const { isSuperuser } = use(CurrentUserContext);

  const menuOpen = Boolean(anchorEl);

  const fileIdsValue = useWatch({ control, name: 'fileIds' }) || [];

  const {
    dialogOpen,
    setDialogOpen,
    fileIds,
    fileNames,
    handleAddFile,
    handleDelete,
    handleClose,
  } = useAssistantFilePicker({
    fileIds: fileIdsValue,
    onChange: (newValue) => setValue('fileIds', newValue),
  });

  return (
    <Grid container spacing={1}>
      {fileIds.length > 0 && (
        <Grid size={12}>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {fileIds.map((id) => (
              <Chip
                key={id}
                label={
                  fileNames.find((file) => file._id === id)?.filename || id
                }
                onDelete={
                  loading || disabled ? undefined : () => handleDelete(id)
                }
                icon={<AttachFile />}
                size="small"
              />
            ))}
          </Stack>
        </Grid>
      )}
      <Grid size={12}>
        <Controller
          name="prompt"
          control={control}
          render={({ field, fieldState }) => {
            const sendButtonDisabled =
              loading || disabled || !field.value.trim();

            return (
              <TextField
                autoFocus={isSmallScreen}
                multiline
                fullWidth
                disabled={loading || disabled}
                id="message"
                size="small"
                {...textFieldProps}
                placeholder={
                  loading
                    ? "I'm working on it!"
                    : disabled
                      ? 'Message limit reached'
                      : textFieldProps.placeholder
                }
                error={Boolean(fieldState.error || errorMessage)}
                helperText={
                  disabled
                    ? 'You have reached the message limit, to send more messages please start a new conversation'
                    : (fieldState.error?.message ??
                      errorMessage ?? (
                        <Stack
                          component="span"
                          direction="row"
                          justifyContent="space-between"
                          alignItems="flex-start"
                          gap={2}
                        >
                          <span>
                            I can make mistakes - do check anything I give you.
                            Your data is safe and never used to train AI models.
                            Learn how GovernorHub{' '}
                            <Or2Link
                              href={`${env.NEXT_PUBLIC_HELP_URL}/en/articles/11164911-ai-assistant-frequently-asked-questions`}
                              isExternal
                            >
                              uses AI
                            </Or2Link>{' '}
                            and{' '}
                            <Or2Link
                              isExternal
                              href={LEGAL_DOCUMENT_URLS.aiFeaturesTerms}
                            >
                              protects your data
                            </Or2Link>
                          </span>
                          {!isSmallScreen && (
                            <Fade in={!sendButtonDisabled}>
                              <Typography
                                component="span"
                                variant="caption"
                                aria-hidden
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                <Box component="kbd" sx={keycapSx}>
                                  {sendShortcutModifier}
                                </Box>{' '}
                                <Box component="kbd" sx={keycapSx}>
                                  ↵
                                </Box>{' '}
                                to send
                              </Typography>
                            </Fade>
                          )}
                        </Stack>
                      ))
                }
                slotProps={{
                  input: {
                    startAdornment: isSuperuser && (
                      <InputAdornment position="start">
                        <Controller
                          name="experimental"
                          control={control}
                          render={({ field }) => {
                            return (
                              <>
                                <Or2Avatar
                                  sx={{
                                    bgcolor: field.value
                                      ? 'warning.main'
                                      : undefined,
                                    cursor: isSuperuser ? 'pointer' : undefined,
                                    height: 32,
                                    width: 32,
                                  }}
                                  onClick={(event) => {
                                    if (!isSuperuser) {
                                      return;
                                    }

                                    setAnchorEl((anchorEl) =>
                                      anchorEl ? null : event.currentTarget,
                                    );
                                  }}
                                >
                                  {field.value ? (
                                    <Science />
                                  ) : (
                                    <SvgIcon
                                      component={owlWandIcon}
                                      alt="AI Assistant"
                                      viewBox="-6 -3 40 40"
                                    />
                                  )}
                                </Or2Avatar>
                                <Popover
                                  disablePortal
                                  anchorEl={anchorEl}
                                  anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                  }}
                                  transformOrigin={{
                                    vertical: 92,
                                    horizontal: 16,
                                  }}
                                  slots={{ transition: Fade }}
                                  slotProps={{
                                    transition: {
                                      onExited: () =>
                                        setPopoverGridDirection(
                                          field.value
                                            ? 'column'
                                            : 'column-reverse',
                                        ),
                                    },
                                  }}
                                  open={menuOpen}
                                  onClose={() => setAnchorEl(null)}
                                >
                                  <Grid
                                    container
                                    direction={popoverGridDirection}
                                  >
                                    <Grid>
                                      <Or2ListItemButton
                                        dense
                                        selected={!field.value}
                                        onClick={() => {
                                          field.onChange(false);
                                          setAnchorEl(null);
                                        }}
                                      >
                                        <ListItemIcon>
                                          <Or2Avatar>
                                            <SvgIcon
                                              component={owlWandIcon}
                                              alt="AI Assistant"
                                              viewBox="-6 -3 40 40"
                                            />
                                          </Or2Avatar>
                                        </ListItemIcon>
                                        <ListItemText>
                                          AI Assistant Beta
                                        </ListItemText>
                                      </Or2ListItemButton>
                                    </Grid>
                                    <Grid>
                                      <Or2ListItemButton
                                        dense
                                        selected={field.value}
                                        onClick={() => {
                                          field.onChange(true);
                                          setAnchorEl(null);
                                        }}
                                      >
                                        <ListItemIcon>
                                          <Or2Avatar
                                            sx={{ bgcolor: 'warning.main' }}
                                          >
                                            <Science />
                                          </Or2Avatar>
                                        </ListItemIcon>
                                        <ListItemText>
                                          AI Assistant Experimental
                                        </ListItemText>
                                      </Or2ListItemButton>
                                    </Grid>
                                  </Grid>
                                </Popover>
                              </>
                            );
                          }}
                        />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          <Box
                            sx={{
                              position: 'relative',
                              display: 'inline-flex',
                            }}
                          >
                            <Tooltip
                              title={
                                fileIdsValue.length >= 5
                                  ? 'Maximum of 5 files allowed'
                                  : "Choose a file from your board's documents to add context"
                              }
                            >
                              <span>
                                <Or2Button
                                  disabled={
                                    loading ||
                                    disabled ||
                                    fileIdsValue.length >= 5
                                  }
                                  size="small"
                                  startIcon={<Add />}
                                  onClick={() => {
                                    trackIntercomEvent(
                                      `Clicked AI Assistant 'Choose File' Button`,
                                    );
                                    setDialogOpen(true);
                                  }}
                                  sx={{ whiteSpace: 'nowrap' }}
                                >
                                  Choose file
                                </Or2Button>
                              </span>
                            </Tooltip>
                          </Box>
                          {loading ? (
                            <CircularProgress size={24} />
                          ) : (
                            <Tooltip
                              title={`Send (${sendShortcutModifier} + Enter)`}
                            >
                              <span>
                                <IconButton
                                  aria-label="Send"
                                  aria-keyshortcuts="Meta+Enter Control+Enter"
                                  disabled={sendButtonDisabled}
                                  edge="end"
                                  onClick={handleSend}
                                  sx={{
                                    p: 0.5,
                                    mr: 0,
                                    ...(!sendButtonDisabled && {
                                      bgcolor: 'primary.main',
                                      color: 'primary.contrastText',
                                      '&:hover': { bgcolor: 'primary.dark' },
                                    }),
                                  }}
                                >
                                  <ArrowForwardIconOutlined fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </Box>
                      </InputAdornment>
                    ),
                  },
                }}
                onKeyDown={(e) => {
                  // Check for Cmd+Enter (Mac) or Ctrl+Enter (Windows/Linux)
                  if (
                    e.key === 'Enter' &&
                    (e.metaKey || e.ctrlKey) &&
                    !sendButtonDisabled
                  ) {
                    e.preventDefault();
                    if (field.value) {
                      handleSend();
                    }
                  }
                }}
                {...field}
              />
            );
          }}
        />
      </Grid>
      <AssistantFilePickerDialog
        open={dialogOpen}
        onClose={handleClose}
        onSelect={handleAddFile}
      />
    </Grid>
  );
};
